import { useContext, useState, useRef } from 'react'
import { useCustomEventListener } from 'react-custom-events'
import classnames from 'classnames/bind'
import { StoreContext } from '~/store'
import { useFormikContext, Field } from 'formik'
import { wrapWithValidation } from '~/utils'

import { useTranslation, Trans } from 'react-i18next'
import i18next from '~/i18n'


import { CloseButton, RadioField, CheckboxField } from '~/components'
import { showErrorModal } from '~/utils'
import * as common from '~/config/common.module.css'
import * as styles from './index.module.css'
import * as panelStyles from '~/components/ui/OptionsPanel/index.module.css'

import { getVoucherValue } from '~/utils/data'
import { formatBookingRequest, formatReadableDateOnly } from '~/utils/format'

const cx = classnames.bind(styles)

const renderVoucherInfo = voucher => {
  let str = i18next.t('expires-at', { date: formatReadableDateOnly(new Date(voucher.expiry)) })
  if (voucher.valueType == 'PERCENTAGE') {
    return <>{str}<br />{i18next.t('info-voucher-max', { amount: voucher.maxPriceToBeDiscounted })}</>
  }
  return str
}

export default function PaymentTypes() {
  const { t } = useTranslation()
  const [isVisible, setIsVisible] = useState(false)

  // const payBooking = ({ bookingId, accountId, success }) => {
  //   if (user?.userId) {
  //     setIsVisible(true)
  //     getPaymentToken(user.userId, bookingId).then((paymentToken) => {
  //       console.log(paymentToken)
  //       const decoded = jwt_decode(paymentToken)
  //       showCashier(payBookingConfig, user.userId, paymentToken, decoded.transactionAmount, accountId, success)
  //     })
  //   }
  // }

  const paymentButtonRef = useRef()
  const payInCarRef = useRef()
  const optionsButtonRef = useRef()
  const messageFieldRef = useRef()
  const nameFieldRef = useRef()
  const [isPaymentOpen, setIsPaymentOpen] = useState(false)
  const [isOptionsOpen, setIsOptionsOpen] = useState(false)
  const [isAddAccountOpen, setIsAddAccountOpen] = useState(false)
  const [isAddVoucherOpen, setIsAddVoucherOpen] = useState(false)
  const [isLoading, setIsLoading] = useState(false)

  const { config, user, paymentAccounts, refreshUser } = useContext(StoreContext)

  const { values, setFieldValue } = useFormikContext()

  const fixedPrice = values.autoValues.fixedPrice !== null ? values.autoValues.fixedPrice : values.fixedPrice

  const disableCreditCards = !values.priceType?.paymentMethods?.includes('CreditCard')
  const disableTSABAccounts = !values.priceType?.paymentMethods?.includes('TSABAccount')
  const disableTCAAccounts = !values.priceType?.paymentMethods?.includes('TravelClearingAccount')
  const disableVouchers = !values.priceType?.voucherEnabled

  const hasAccounts = user?.accounts?.length > 0
  const hasCreditCards = paymentAccounts?.length > 0
  const hasVouchers = user?.vouchers?.length > 0

  const makeRequest = () => {
    const isCreditCard = values.payment.indexOf('CreditCard') !== -1
    const bookingFunc = isCreditCard ? prepareBooking : createBooking
    
    setIsLoading(true)
    bookingFunc(values, user.userId).then((response) => {
      console.log(response)
      if (!isNaN(response?.bookingId)) {
        if (isCreditCard) {
          emitCustomEvent(
            'CASHIER_PAY_BOOKING', 
            {
              bookingId: response.bookingId,
              accountId: values.account.accountId, 
              success: () => handleBookingComplete(response),
              doneLoading: () => setIsLoading(false)
            }
          )
        }
        else {
          handleBookingComplete(response)
        }
      }
    })
    .catch((error) => {
      if (error?.response?.error === 'INVALID_PHONE_NUMBER') {
        setFieldError('phone', error?.response?.message)
      }
      else if (!(error?.response?.status === 401 && error?.response?.error === 'Unauthorized')) {
        console.error(error)
        showErrorModal(error?.response?.message)
      }
      setIsLoading(false)
    })
    // .finally(() => {
    //   setIsLoading(false)
    // })
  }


  const sendToGTM = (values, bookingId) => {
    const booking = formatBookingRequest(values);
    const priceComponents = fixedPrice ? values.priceEstimate?.prices?.filter((value) => {return value.serviceType === booking.serviceType}) : false;
    const prices = priceComponents && priceComponents.length > 0 ? priceComponents[0].priceComponents?.filter((value) => {return value.type === 'fixedPrice'}) : false;
    const price = prices && prices.length > 0 ? prices[0].amount : false;
    const trackData = {
      price: price,
      preBook: values.pickupTime ? true : false,
      pickupTime: booking.pickUpTimeStamp,
      bookingNumber: bookingId,
      carType: booking.serviceType,
      extras: booking.vehicleOptions,
      fixedPrice: fixedPrice && booking.priceToken ? true : false
    };
    track('confirmBooking', trackData);
  }

  const wrappedNext = wrapWithValidation(makeRequest, useFormikContext())

  const setAccount = account => {
    if (!account) {
      setFieldValue('accountReference', '', false)
    }
    setFieldValue('account', account, false)
  }

  const openOptions = () => {
    setIsOptionsOpen(true)
    setTimeout(() => {
      if (messageFieldRef.current) messageFieldRef.current.focus()
    }, 200)
  }

  const closeOptions = () => {
    setIsOptionsOpen(false)
    if (optionsButtonRef.current) optionsButtonRef.current.focus()
  }

  const openPayment = () => {
    setIsPaymentOpen(true)
    setTimeout(() => {
      if (payInCarRef.current) payInCarRef.current.focus()
    }, 200)
  }

  const closePayment = () => {
    setIsPaymentOpen(false)
    if (paymentButtonRef.current) paymentButtonRef.current.focus()
  }

  const addCard = () => {
    closeOptions()
    emitCustomEvent('CASHIER_ADD_CARD', { success: refreshUser })
  }

  const openAddAccount = () => {
    setIsAddAccountOpen(true)
  }

  const closeAddAccount = () => {
    setIsAddAccountOpen(false)
  }

  const openAddVoucher = voucher => {
    setIsAddVoucherOpen(true)
  }

  const closeAddVoucher = () => {
    setIsAddVoucherOpen(false)
  }

  const closeModal = () => {
    setIsVisible(false)
  }

  // useCustomEventListener('SHOW_IFRAME', openRedirect)
  // useCustomEventListener('CASHIER_ADD_CARD', addCard)
  // useCustomEventListener('CASHIER_PAY_BOOKING', payBooking)

  return (
    <div className={cx(styles.wrapper, { isVisible })}>
      <div className={cx(styles.container, { })}>
        
            <div className={cx(common.contentBottom, { fullscreen:true, contentOptions:true, noBackground: true })}>
              <Field type="radio" name="payment" title={t('title-pay-in-car')} value="PayInCar" onClick={()=>setAccount(false)} innerRef={payInCarRef} component={RadioField} />
              <div className={cx(styles.subHeader, styles.marginTop, common.fullWidth)}>{t('header-card')}</div>
              {!hasCreditCards && <div className={styles.instructions}>{t('info-no-cards')}</div>}
              {paymentAccounts?.map((account, index) => (
                <Field 
                  key={index} 
                  type="radio" 
                  name="payment" 
                  title={account.maskedAccount} 
                  info={account.cardType} 
                  value={`CreditCard-${account.accountId}`} 
                  onClick={()=>setAccount(account)} 
                  tabIndex={isPaymentOpen ? 0 : -1} 
                  disabled={disableCreditCards } 
                  component={RadioField} 
                />
              ))}
              <div className={cx(styles.instructions, common.fullWidth)}>
                <button className={styles.button} onClick={addCard} tabIndex={isPaymentOpen ? 0 : -1}>+ {t('button-add-new-card')}</button>
              </div>
              {!hasAccounts && <div className={styles.instructions}>{t('info-no-accounts')}</div>}
              {user?.accounts?.map((account, index) => (
                <Field 
                  key={index} 
                  type="radio" 
                  name="payment" 
                  title={account.accountName} 
                  info={account.accountNumber} 
                  value={`Account-${account.accountNumber}`} 
                  onClick={()=>setAccount(account)} 
                  tabIndex={isPaymentOpen ? 0 : -1}
                  disabled={
                    (account.type === 'TSAB' && disableTSABAccounts ) 
                    || (account.type === 'TCA' && disableTCAAccounts)
                  }
                  component={RadioField} 
                />
              ))}
              <div className={cx(styles.instructions, common.fullWidth)}>
                <button className={styles.button} onClick={openAddAccount} tabIndex={isPaymentOpen ? 0 : -1}>+ {t('button-add-new-account')}</button>
              </div>
            </div>
          
            <div className={panelStyles.title}>{t('title-vouchers')}</div>
            <div className={cx(common.contentBottom, { contentOptions: true, noPadding: true })}>
              {!hasVouchers && <div className={styles.instructions}>{t('info-no-vouchers')}</div>}
              {user?.vouchers?.map((voucher, index) => (
                <Field 
                  key={index} 
                  name="vouchers" 
                  value={voucher.voucherCode} 
                  title={`${voucher.voucherCode} - ${getVoucherValue(voucher)}`}
                  info={renderVoucherInfo(voucher)}
                  disabled={disableVouchers || (values?.vouchers?.length > 0 && !values?.vouchers?.includes(voucher.voucherCode))} 
                  tabIndex={isPaymentOpen ? 0 : -1}
                  component={CheckboxField} 
                  larger
                />
              ))}
              <div className={cx(styles.instructions, common.fullWidth)}>
                <button className={styles.button} onClick={openAddVoucher} tabIndex={isPaymentOpen ? 0 : -1}>+ {t('button-add-new-voucher')}</button>
              </div>
              {hasVouchers && <div className={cx(styles.instructions, common.fullWidth)}>
                <br />
                <Trans 
                  i18nKey={disableVouchers ? 'info-voucher-codes-partner' : 'info-voucher-codes'}
                  components={{
                    link1: <a href={config.paymentTermsLink} target="_blank"/>
                  }}
                />
            </div>}
            </div>
          
          {isAddAccountOpen && <Modal onClose={closeAddAccount} isOpen>
            <AddAccount onComplete={closeAddAccount} />
          </Modal>}
          {isAddVoucherOpen && <Modal onClose={closeAddVoucher} isOpen>
            <AddVoucher onComplete={closeAddVoucher} />
          </Modal>}
        
        <CloseButton onClick={closeModal} />
      </div>
    </div>
  )
}
